import { useMutation } from '@tanstack/react-query'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { ProjectUserRole } from '@maxus/common'
import { queryClient, trpc, trpcClient } from '@maxus/website/lib'
import { cn } from '@maxus/website/utils'

import { useDrawer } from '../../hooks/use-drawer'
import { useNotification } from '../notification'
import { MemberPanel } from './member-panel'

type MemberCallback = {
  id: string
  role: ProjectUserRole
}

type MemberDrawerMember = {
  id: string
  name: string
  avatar: string
  desc: string
  role: ProjectUserRole
}

export type MemberDrawerParams = {
  existingMembers: MemberDrawerMember[]
  onConfirm: (props: { added: MemberCallback[]; updated: MemberCallback[]; removed: MemberCallback[] }) => void
}

export type MemberDrawerHandle = {
  open: (params: MemberDrawerParams) => void
}

export type ProjectMemberDrawerItem = {
  id: string
  name: string
  avatarUrl: string | null
  email?: string | null
  department?: string | null
  role: ProjectUserRole
}

export const useProjectMemberDrawer = () => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const memberDrawerRef = useRef<MemberDrawerHandle>(null)

  const batchUpdateMembers = useMutation({
    mutationFn: (input: {
      projectId: string
      toAdd: { id: string; role: ProjectUserRole }[]
      toUpdate: { id: string; role: ProjectUserRole }[]
      toRemove: { id: string }[]
    }) => trpcClient.project.projectUser.batchUpdate.mutate(input),
    onSuccess: (_data, { projectId }) => {
      notify({ title: t('Saved'), description: t('Member changes saved.') })
      return Promise.all([
        queryClient.invalidateQueries(trpc.project.get.queryFilter({ projectId })),
        queryClient.invalidateQueries(trpc.project.list.queryFilter()),
        queryClient.invalidateQueries(trpc.project.deliverables.get.queryFilter()),
      ])
    },
    onError: error => {
      const description =
        error instanceof Error && error.message ? error.message : 'Save failed. Please try again later.'
      notify({ status: 'error', title: t('Failed'), description: t(description) })
    },
  })

  const openProjectMemberDrawer = ({
    projectId,
    members,
  }: {
    projectId: string
    members: ProjectMemberDrawerItem[]
  }) => {
    memberDrawerRef.current?.open({
      existingMembers: members.map(member => ({
        id: member.id,
        name: member.name,
        avatar: member.avatarUrl ?? '',
        desc: member.department || member.email || '',
        role: member.role,
      })),
      onConfirm: ({ added, updated, removed }) => {
        batchUpdateMembers.mutate({
          projectId,
          toAdd: added.map(member => ({ id: member.id, role: member.role })),
          toUpdate: updated.map(member => ({ id: member.id, role: member.role })),
          toRemove: removed.map(member => ({ id: member.id })),
        })
      },
    })
  }

  return { memberDrawerRef, openProjectMemberDrawer }
}

export const MemberDrawer = forwardRef<MemberDrawerHandle>((_, ref) => {
  const {
    active,
    entered,
    closing,
    close,
    complete,
    open,
    params,
  } = useDrawer<MemberDrawerParams>()

  useImperativeHandle(
    ref,
    () => ({
      open: nextParams => open(nextParams),
    }),
  )

  if (!active || !params) {
    return null
  }

  const confirm = (props: { added: MemberCallback[]; updated: MemberCallback[]; removed: MemberCallback[] }) => {
    complete(() => params.onConfirm(props))
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-end bg-black/40 transition-colors',
        (!entered || closing) && 'bg-black/0',
      )}
      onPointerDown={event => {
        if (event.target === event.currentTarget) {
          close()
        }
      }}
    >
      <div
        className={cn(
          'h-full w-125 transition-transform duration-[240ms] ease-out',
          (!entered || closing) && 'translate-x-full',
        )}
        onClick={event => event.stopPropagation()}
        role="dialog"
      >
        <MemberPanel existingMembers={params.existingMembers} onClose={close} onConfirm={confirm} />
      </div>
    </div>
  )
})

MemberDrawer.displayName = 'MemberDrawer'
